import { useAuthQuery } from './useAuthQuery';
import { supabase } from '@/integrations/supabase/client';
import { chunkedInQuerySimple } from '@/lib/queries/chunkedInQuery';

interface ProjectOption {
  id: string;
  project_name: string;
}

// Strict projects list for Items: only owner's projects or ones with explicit per-user can_view permission.
// Admins see all active projects.
export const useItemsProjectsStrict = () => {
  return useAuthQuery<ProjectOption[]>({
    queryKey: ['items-projects-strict'],
    queryFn: async () => {
      // Get current user
      const { data: userRes, error: userErr } = await supabase.auth.getUser();
      if (userErr) throw userErr;
      const userId = userRes.user?.id;
      if (!userId) return [];

      // Check if admin
      const { data: isAdmin, error: adminErr } = await supabase.rpc('is_admin', { user_id: userId });
      if (adminErr) throw adminErr;

      if (isAdmin) {
        const { data, error } = await supabase
          .from('projects')
          .select('id, project_name')
          .eq('is_active', true)
          .order('project_name');
        if (error) throw error;
        return data || [];
      }

      // Fetch explicit per-user permissions
      const { data: perms, error: permsErr } = await supabase
        .from('project_user_permissions')
        .select('project_id')
        .eq('user_id', userId)
        .eq('can_view', true);
      if (permsErr) throw permsErr;

      const permittedIds = (perms || []).map((p) => p.project_id).filter(Boolean) as string[];

      // Fetch projects owned by current user
      const { data: ownerProjects, error: ownerErr } = await supabase
        .from('projects')
        .select('id')
        .eq('user_id', userId)
        .eq('is_active', true);
      if (ownerErr) throw ownerErr;

      const ownerIds = (ownerProjects || []).map((p) => p.id);

      const allIds = Array.from(new Set([...permittedIds, ...ownerIds]));

      if (allIds.length === 0) return [];

      // Use chunkedInQuerySimple for fetching projects
      const projects = await chunkedInQuerySimple<ProjectOption>(
        supabase,
        'projects',
        'id',
        allIds,
        'id, project_name',
        (query) => query.eq('is_active', true).order('project_name')
      );

      return projects;
    },
    requiresAuth: true,
    requiresSession: true,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};